import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { uploadAndCreateImageEntry } from "../../../../../utils/createImgforLots";
import { uploadAndCreateDocumentEntry } from "../../../../../utils/createPdfforLot";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "../../../../../redux/actions/modalActions";
import { fetchProgrammes } from "../../../../../redux/actions/immobilierActions";
import mainAxios from "../../../../../redux/api/mainAxios";
import Select from "react-select";
import { createPaginationState, customStyles, getVisibleOptions, handleInputChange, handleScrollToBottom } from "../../../componnents/deepsearchUtils";
import { fetchInitialData } from "../../../../../redux/actions/deepsearchActions";

const AddProgramme = () => {
  const dispatch = useDispatch();
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedImageEntries, setUploadedImageEntries] = useState([]);
  const [uploadedDocumentEntries, setUploadedDocumentEntries] = useState([]);
  const [documentTypes] = useState([
    { label: "règlement", value: "règlement" },
    { label: "plan de masse", value: "plan_masse" },
    { label: "plan de vente", value: "plan_vente" },
    { label: "plan etage", value: "plan_etage" },
    { label: "plaquette commerciale", value: "plaquette_commerciale" },
    { label: "contrat de réservation", value: "contrat_reservation" },
    { label: "autre", value: "other_documents" },
  ]);
  const [formData, setFormData] = useState({
    name: null,
    address: null,
    progress: null,
    description: null,
    latitude: null,
    longitude: null,
    delivery_date: null,
    is_actable: false,
    actibility_date: null,
    origine: null,
    promoteur: null,
    city: null,
    lastSync: null,
    original_id: null,
    kind: null,
    seniority: null,
    nf_habitat_type: null,
    nf_habitat_status: null,
    nf_habitat_certificat: null,
    url: null,
    is_only_restricted: false,
    has_commercial_offer: false,
  });

  const { lieu, promoteur } = useSelector((state) => state.deepsearchReducer);

  useEffect(() => {
    dispatch(fetchInitialData());
  }, [dispatch]);

  // Add pagination state for lieu select
  const [lieuPagination, setLieuPagination] = useState(createPaginationState(30));

  const visibleLieuOptions = useMemo(() => {
    return getVisibleOptions(lieu, lieuPagination, 'name', 'id', 'name');
  }, [lieu, lieuPagination]);
  const handleLieuScrollToBottom = () => {
    handleScrollToBottom(lieu, lieuPagination, setLieuPagination, 'name');
  };
  // Handle lieu search input change
  const handleLieuInputChange = (inputValue) => {
    handleInputChange(inputValue, setLieuPagination);
  };
  // Handle document selection
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedDocuments((prev) => [
      ...prev,
      ...files.map((file) => ({
        file,
        type: "autre", // Default type
      })),
    ]);
  };

  // Remove document from selection
  const removeDocument = (index) => {
    setSelectedDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  // Update document type
  const updateDocumentType = (index, type) => {
    setSelectedDocuments((prev) =>
      prev.map((doc, i) => (i === index ? { ...doc, type } : doc))
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages((prev) => [...prev, ...files]);
  };

  // Remove image from selection
  const removeImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleImageUpload = async () => {
    setUploading(true);
    const uploadedEntries = [];

    try {
      // Process each selected image
      for (const file of selectedImages) {
        const reference = formData.name || `temp-${Date.now()}`;
        const entry = await uploadAndCreateImageEntry(file, reference);
        if (entry) {
          uploadedEntries.push(entry);
        }
      }

      setUploadedImageEntries(uploadedEntries);
      toast.success(
        `${uploadedEntries.length} image(s) téléchargé(s) avec succès`
      );
      return uploadedEntries;
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Failed to upload one or more images");
      return [];
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async () => {
    setUploading(true);
    const uploadedEntries = [];

    try {
      // Process each selected document
      for (const doc of selectedDocuments) {
        const reference = formData.name || `temp-${Date.now()}`;
        const entry = await uploadAndCreateDocumentEntry(
          doc.file,
          reference,
          doc.type
        );
        if (entry) {
          uploadedEntries.push(entry);
        }
      }

      setUploadedDocumentEntries(uploadedEntries);
      toast.success(
        `${uploadedEntries.length} document(s) téléchargé(s) avec succès`
      );
      return uploadedEntries;
    } catch (error) {
      console.error("Error uploading documents:", error);
      toast.error("Failed to upload one or more documents");
      return [];
    } finally {
      setUploading(false);
    }
  };

  // Submit form with images and documents already uploaded
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setUploading(true);

      // Upload images if they haven't been uploaded yet
      let imageEntries = uploadedImageEntries;
      if (selectedImages.length > 0 && uploadedImageEntries.length === 0) {
        imageEntries = await handleImageUpload();
      }

      // Upload documents if they haven't been uploaded yet
      let documentEntries = uploadedDocumentEntries;
      if (
        selectedDocuments.length > 0 &&
        uploadedDocumentEntries.length === 0
      ) {
        documentEntries = await handleDocumentUpload();
      }

      // Prepare the programme data with image and document links
      const dataToSend = {
        ...formData,
        lastSync: new Date().toISOString(),
        // Add the image IDs to connect with the programme
        imageIds: imageEntries.map((entry) => entry.id),
        // Add the document IDs to connect with the programme
        documentIds: documentEntries.map((entry) => entry.id),
      };

      const response = await mainAxios.post(`api/add-programme`, dataToSend);
      toast.success("Programme ajouté avec succès");

      // Reset form after successful submission
      setFormData({
        name: null,
        address: null,
        progress: null,
        description: null,
        latitude: null,
        longitude: null,
        delivery_date: null,
        is_actable: false,
        actibility_date: null,
        origine: null,
        zone_fiscale: null,
        promoteur: null,
        city: null,
        lastSync: null,
        original_id: null,
        slug: null,
        kind: null,
        seniority: null,
        nf_habitat_type: null,
        nf_habitat_status: null,
        nf_habitat_certificat: null,
        url: null,
        is_only_restricted: false,
        has_commercial_offer: false,
      });
      setSelectedImages([]);
      setUploadedImageEntries([]);
      setSelectedDocuments([]);
      setUploadedDocumentEntries([]);
    } catch (error) {
      console.error("Erreur lors de l'ajout du programme:", error);
      toast.error("Erreur lors de l'ajout du programme");
    } finally {
      setUploading(false);
      dispatch(fetchProgrammes());
      dispatch(closeModal());
    }
  };

  // Add pagination state for promoteur select
  const [promoteurPagination, setPromoteurPagination] = useState(createPaginationState(30));

  const visiblePromoteurOptions = useMemo(() => {
    return getVisibleOptions(promoteur, promoteurPagination, 'name', 'id', 'name');
  }, [promoteur, promoteurPagination]);
  const handlePromoteurScrollToBottom = () => {
    handleScrollToBottom(promoteur, promoteurPagination, setPromoteurPagination, 'name');
  };
  // Handle promoteur search input change
  const handlePromoteurInputChange = (inputValue) => {
    handleInputChange(inputValue, setPromoteurPagination);
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit}>
        <div className="d-flex gap10">
          <div className="w60">
            <div className="form-row">
              <div className="form-group">
                <label>Nom du Programme*</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Adresse</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ville</label>
                <div className="search-dropdown-container">
                  <Select
                    styles={customStyles}
                    isMulti={false}
                    options={visibleLieuOptions}
                    value={formData.city ? visibleLieuOptions.find(option => option.value === formData.city) : null}
                    maxMenuHeight={150}
                    onChange={(selectedOption) => {
                      setFormData((prev) => ({
                        ...prev,
                        city: selectedOption?.value || null,
                      }));
                    }}
                    onMenuScrollToBottom={handleLieuScrollToBottom}
                    onInputChange={handleLieuInputChange}
                    placeholder="Selectionner..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? `Aucun lieu trouvé pour "${inputValue}"` : "Tapez pour rechercher"
                    }
                    isSearchable={true}
                    isClearable={true}
                    filterOption={null} // Disable built-in filtering since we handle it manually
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Promoteur</label>
                <div className="search-dropdown-container">
                  <Select
                    styles={customStyles}
                    isMulti={false}
                    options={visiblePromoteurOptions}
                    value={formData.promoteur ? visiblePromoteurOptions.find(option => option.value === formData.promoteur) : null}
                    maxMenuHeight={150}
                    onChange={(selectedOption) => {
                      setFormData((prev) => ({
                        ...prev,
                        promoteur: selectedOption?.value || null,
                      }));
                    }}
                    onMenuScrollToBottom={handlePromoteurScrollToBottom}
                    onInputChange={handlePromoteurInputChange}
                    placeholder="Selectionner..."
                    noOptionsMessage={({ inputValue }) =>
                      inputValue ? `Aucun promoteur trouvé pour "${inputValue}"` : "Tapez pour rechercher"
                    }
                    isSearchable={true}
                    isClearable={true}
                    filterOption={null} // Disable built-in filtering since we handle it manually
                  />

                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Latitude</label>
                <input
                  type="number"
                  name="latitude"
                  value={formData.latitude || ""}
                  onChange={handleChange}
                  step="any"
                />
              </div>
              <div className="form-group">
                <label>Longitude</label>
                <input
                  type="number"
                  name="longitude"
                  value={formData.longitude || ""}
                  onChange={handleChange}
                  step="any"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date de livraison</label>
                <input
                  type="date"
                  name="delivery_date"
                  value={formData.delivery_date || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Date d'actabilité</label>
                <input
                  type="date"
                  name="actibility_date"
                  value={formData.actibility_date || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Origine</label>
                <Select
                  styles={customStyles}
                  isMulti={false}
                  options={[
                    { label: "Interne", value: "interne" },
                    { label: "Externe", value: "externe" },
                  ]}
                  value={formData.origine ? { label: formData.origine === "interne" ? "Interne" : "Externe", value: formData.origine } : null}
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      origine: selectedOption?.value || null,
                    }));
                  }}
                  placeholder="Sélectionner..."
                  isClearable={true}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type</label>
                <input
                  type="text"
                  name="kind"
                  value={formData.kind || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Ancienneté</label>
                <input type="text" name="seniority" value={formData.seniority || ""} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Type NF Habitat</label>
                <Select
                  styles={customStyles}
                  isMulti={false}
                  options={[
                    { label: "Non certifié", value: "not_certified" },
                    { label: "NF Habitat", value: "nf_habitat" },
                  ]}
                  value={formData.nf_habitat_type ? { label: formData.nf_habitat_type === "not_certified" ? "Non certifié" : "NF Habitat", value: formData.nf_habitat_type } : { label: "Non certifié", value: "not_certified" }}
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      nf_habitat_type: selectedOption?.value || "not_certified",
                    }));
                  }}
                  placeholder="Sélectionner..."
                  isClearable={false}
                />
              </div>
              <div className="form-group">
                <label>Statut NF Habitat</label>
                <Select
                  styles={customStyles}
                  isMulti={false}
                  options={[
                    { label: "Non obtenu", value: "not_obtained" },
                    { label: "En cours", value: "in_progress" },
                    { label: "Obtenu", value: "obtained" },
                  ]}
                  value={formData.nf_habitat_status ? [
                    { label: "Non obtenu", value: "not_obtained" },
                    { label: "En cours", value: "in_progress" },
                    { label: "Obtenu", value: "obtained" },
                  ].find(option => option.value === formData.nf_habitat_status) : { label: "Non obtenu", value: "not_obtained" }}
                  onChange={(selectedOption) => {
                    setFormData((prev) => ({
                      ...prev,
                      nf_habitat_status: selectedOption?.value || "not_obtained",
                    }));
                  }}
                  placeholder="Sélectionner..."
                  isClearable={false}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Certificat NF Habitat</label>
                <input
                  type="text"
                  name="nf_habitat_certificat"
                  value={formData.nf_habitat_certificat || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>URL</label>
                <input
                  type="url"
                  name="url"
                  value={formData.url || ""}
                  onChange={handleChange}
                  placeholder="https://..."
                />
              </div>

            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description || ""}
                  onChange={handleChange}
                  rows="4"
                  className="form-control"
                ></textarea>
              </div>
            </div>
            <div className="form-col mb-5">

              <div className="form-group ">
                <label className="d-flex gap-2">
                  <input
                    type="checkbox"
                    name="is_actable"
                    checked={formData.is_actable}
                    onChange={handleChange}
                  />
                  Actable
                </label>
              </div>
              <div className="form-group">
                <label className="d-flex gap-2">
                  <input
                    type="checkbox"
                    name="is_only_restricted"
                    checked={formData.is_only_restricted}
                    onChange={handleChange}
                  />
                  Uniquement restreint
                </label>
              </div>
              <div className="form-group">
                <label className="d-flex gap-2">
                  <input
                    type="checkbox"
                    name="has_commercial_offer"
                    checked={formData.has_commercial_offer}
                    onChange={handleChange}
                  />
                  Offre commerciale disponible
                </label>
              </div>
            </div>


          </div>

          <div className="w40">
            {/* Images Section */}
            <div className="form-group d-flex flex-column">
              <label>Images</label>

              {/* Custom French button that triggers the hidden input */}
              <button
                type="button"
                className="radius5 px-20 py-10 bg-pink text-white border-0 hoverOpacity"
                onClick={() =>
                  document.getElementById("image-input-program").click()
                }
              >
                Sélectionner des images
              </button>

              {/* Hidden actual file input */}
              <input
                id="image-input-program"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="d-none"
              />

              {selectedImages.length > 0 && (
                <div className="selected-images d-flex flex-column gap10 mt-2">
                  <p className="fw-bold mb-2">
                    Images sélectionnées ({selectedImages.length}):
                  </p>
                  <div className="image-previews d-flex flex-wrap gap10">
                    {selectedImages.map((file, index) => (
                      <div
                        key={index}
                        className="image-preview-item position-relative p-2 border rounded bg-light"
                      >
                        <p className="lot-image-name mb-0">{file.name}</p>
                        <button
                          type="button"
                          className="remove-image-btn position-absolute top-0 end-0 btn btn-sm text-danger"
                          onClick={() => removeImage(index)}
                          aria-label="Supprimer cette image"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {uploadedImageEntries.length > 0 && (
                    <div className="upload-success mt-2">
                      <p className="text-success">
                        {uploadedImageEntries.length} image(s) téléchargée(s)
                        avec succès
                      </p>
                    </div>
                  )}

                  {!formData.name && selectedImages.length > 0 && (
                    <p className="text-warning mt-2">
                      Veuillez remplir le champ "Nom du Programme" avant de
                      télécharger les images
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Documents Section */}
            <div className="form-group d-flex flex-column mt-4">
              <label>Documents PDF</label>

              {/* Custom French button that triggers the hidden input */}
              <button
                type="button"
                className="radius5 px-20 py-10 bg-pink text-white border-0 hoverOpacity"
                onClick={() =>
                  document.getElementById("document-input-program").click()
                }
              >
                Sélectionner des documents PDF
              </button>

              {/* Hidden actual file input */}
              <input
                id="document-input-program"
                type="file"
                accept=".pdf"
                multiple
                onChange={handleDocumentSelect}
                className="d-none"
              />

              {selectedDocuments.length > 0 && (
                <div className="selected-documents d-flex flex-column gap10 mt-2">
                  <p className="fw-bold mb-2">
                    Documents sélectionnés ({selectedDocuments.length}):
                  </p>
                  <div className="document-previews d-flex flex-wrap gap10">
                    {selectedDocuments.map((doc, index) => (
                      <div
                        key={index}
                        className="document-preview-item position-relative p-2 border rounded bg-light"
                      >
                        <p className="lot-document-name mb-2">
                          {doc.file.name}
                        </p>
                        <div className="document-type-selector mb-1">
                          <select
                            value={doc.type}
                            onChange={(e) =>
                              updateDocumentType(index, e.target.value)
                            }
                            className="form-select form-select-sm"
                          >
                            {documentTypes.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="remove-image-btn position-absolute top-0 end-0 btn btn-sm text-danger"
                          onClick={() => removeDocument(index)}
                          aria-label="Supprimer ce document"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>

                  {uploadedDocumentEntries.length > 0 && (
                    <div className="upload-success mt-2">
                      <p className="text-success">
                        {uploadedDocumentEntries.length} document(s)
                        téléchargé(s) avec succès
                      </p>
                    </div>
                  )}

                  {!formData.name && selectedDocuments.length > 0 && (
                    <p className="text-warning mt-2">
                      Veuillez remplir le champ "Nom du Programme" avant de
                      télécharger les documents
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={uploading}
          className={`radius5 px-20 py-10 text-white border-0 hoverOpacity ${uploading ? "bg-soft-grey" : "bg-pink"
            }`}
        >
          {uploading ? "En cours..." : "Enregistrer le programme"}
        </button>
      </form>
    </div>
  );
};

export default AddProgramme;
